import Edit from "@/assets/icons/edit.svg";
import { Button } from "@/components/ui/button";
import EmailAttachment from "@/domain/entities/emails/EmailAttachement";
import useSetMailEditor from "@/hooks/useSetMailEditor";
import useSuggestion from "@/hooks/useSuggestion";
import { setOpenedEvent } from "@/stores/eventStore";
import { setSettingsDialogOpen } from "@/stores/settingsStore";
import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import MailInput from "../writeMail/MailInput";

const NewMessageButton = () => {
    const { t } = useTranslation();

    const [attachments, setAttachments] = useState<EmailAttachment[]>([]);
    const [isNewMessageDialogOpen, setIsNewMessageDialogOpen] = useState(false);
    const toInputRef = useRef<HTMLInputElement>(null);
    const { editor, suggestionEditor } = useSetMailEditor(setAttachments);
    const [pendingSuggestion, setPendingSuggestion] = useState<boolean>(false);

    const {
        acceptOrRefuseSuggestion,
        isLoading: isLoadingSuggestion,
        isPendingChange,
        makeSuggestion,
    } = useSuggestion({
        editor,
        suggestionEditor,
        setPendingSuggestion,
    });

    const handleFocusOnToInput = () => {
        if (toInputRef.current) {
            toInputRef.current.focus();
        }
    };

    const closeNewMessageDialog = () => {
        if (pendingSuggestion) {
            acceptOrRefuseSuggestion(false);
        }
        setIsNewMessageDialogOpen(false);
    };

    const handleOpenNewMessageDialog = () => {
        // MailInput portals to <body>; closing the Settings dialog first
        // avoids being caught by its Radix scroll lock.
        setSettingsDialogOpen(false);
        setOpenedEvent(undefined);
        setIsNewMessageDialogOpen(true);
        handleFocusOnToInput();
    };

    return (
        <div>
            <Button
                className="h-11 rounded-full bg-primary-dark px-3 font-body text-sm font-semibold text-white transition-colors hover:bg-primary-darker md:px-5"
                icon={
                    <img alt="Assistant Logo" className="h-5 w-5" src={Edit} />
                }
                onClick={handleOpenNewMessageDialog}
                size="blank"
                variant="invisible"
            >
                <span className="hidden md:inline">
                    {t("dashboard.newMessage")}
                </span>
            </Button>
            {editor &&
                suggestionEditor &&
                createPortal(
                    <MailInput
                        acceptOrRefuseSuggestion={acceptOrRefuseSuggestion}
                        attachments={attachments}
                        closeDialog={closeNewMessageDialog}
                        editor={editor}
                        isLoading={isLoadingSuggestion}
                        isPendingChange={isPendingChange}
                        makeSuggestion={makeSuggestion}
                        open={isNewMessageDialogOpen}
                        pendingSuggestion={pendingSuggestion}
                        setAttachments={setAttachments}
                        setPendingSuggestion={setPendingSuggestion}
                        suggestionEditor={suggestionEditor}
                        toInputRef={toInputRef}
                        isNewMessage
                    />,
                    document.body,
                )}
        </div>
    );
};

export default NewMessageButton;
